import { CheckIcon, User01Icon } from '@/components/ui/icons'

export interface ContentDiff {
	hasDiff: boolean
	fieldSize: 'sm' | 'md' | 'lg'
}

const SM_HEIGHT = 92
const MD_HEIGHT = 135
const LG_HEIGHT = 156

const CONSECUTIVE_SEPARATOR_HEIGHT_SM = 12
const CONSECUTIVE_SEPARATOR_HEIGHT_MD = 20
const SUB_SECTION_SEPARATOR_HEIGHT = 52
const SECTION_SEPARATOR_HEIGHT = 104

const getDiffSectionHeight = (size: 'sm' | 'md' | 'lg') => {
	if (size === 'sm') {
		return SM_HEIGHT
	}
	if (size === 'md') {
		return MD_HEIGHT
	}
	return LG_HEIGHT
}

const getSeparatorHeight = (
	fieldType: 'consecutive-sm' | 'consecutive-md' | 'sub-section' | 'section'
) => {
	if (fieldType === 'consecutive-sm') {
		return CONSECUTIVE_SEPARATOR_HEIGHT_SM
	}
	if (fieldType === 'consecutive-md') {
		return CONSECUTIVE_SEPARATOR_HEIGHT_MD
	}
	if (fieldType === 'sub-section') {
		return SUB_SECTION_SEPARATOR_HEIGHT
	}
	return SECTION_SEPARATOR_HEIGHT
}

export const DiffSectionWrapper = ({
	children
}: {
	children: React.ReactNode
}) => {
	return (
		<div className='relative h-full w-0.5 overflow-visible'>
			<div className='flex h-full w-0.5 flex-col bg-neutral-200 pt-[178px] pb-12'>
				<div className='flex w-full flex-col'>{children}</div>
			</div>
		</div>
	)
}

interface DiffSectionProps {
	hasDiff: boolean
	size: 'sm' | 'md' | 'lg'
}

export const DiffSection = ({
	hasDiff = false,
	size = 'md'
}: DiffSectionProps) => {
	return (
		<div
			className={`relative flex w-0.5 flex-row items-center justify-center overflow-visible ${
				hasDiff ? 'bg-warning-600' : 'bg-success-600'
			}`}
			style={{ height: `${getDiffSectionHeight(size)}px` }}
		>
			<DiffIndicator hasDiff={hasDiff} />
		</div>
	)
}

interface DiffIndicatorProps {
	hasDiff: boolean
	size?: number
}

export const DiffIndicator = ({ hasDiff, size = 24 }: DiffIndicatorProps) => {
	return (
		<div
			className={`absolute flex items-center justify-center rounded-full text-neutral-white ${
				hasDiff ? 'bg-warning-600' : 'bg-success-600'
			}`}
			style={{ width: `${size}px`, height: `${size}px` }}
		>
			{hasDiff ? (
				<User01Icon className='fill-neutral-white' size={16} />
			) : (
				<CheckIcon className='fill-neutral-white' size={16} />
			)}
		</div>
	)
}
DiffIndicator.displayName = 'DiffIndicator'

type FieldsDiffsSeparatorProps =
	| {
			fieldType: 'consecutive-sm' | 'consecutive-md'
			hasUpperDiff: boolean
			hasLowerDiff: boolean
	  }
	| {
			fieldType: 'section' | 'sub-section'
			hasUpperDiff?: undefined
			hasLowerDiff?: undefined
	  }

export const FieldsDiffsSeparator = ({
	fieldType,
	hasUpperDiff,
	hasLowerDiff
}: FieldsDiffsSeparatorProps) => {
	return (
		<div
			className='flex flex-col'
			style={{ height: `${getSeparatorHeight(fieldType)}px` }}
		>
			{(fieldType === 'consecutive-sm' ||
				fieldType === 'consecutive-md') && (
				<>
					<div
						className={`h-full w-0.5 ${hasUpperDiff ? 'bg-warning-600' : 'bg-success-600'}`}
					/>
					<div
						className={`h-full w-0.5 ${hasLowerDiff ? 'bg-warning-600' : 'bg-success-600'}`}
					/>
				</>
			)}
		</div>
	)
}
FieldsDiffsSeparator.displayName = 'FieldsDiffsSeparator'
